import { FormProvider, useForm } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import {
  CustomerAddress,
  OutletAddress,
  CustomerDeliveryAddress,
  OrderDelivery,
} from '@ancon/wildcat-types'

import { outletSelectedOutletAddressSelector } from '../../outlet/store/outletSelector'
import { clientContextCustomerSelector } from '../../clientContext/store/clientContextSelectors'
import { AppTestIds, StockholmCoordinates } from '../../app/constants'
import { DeliveryLocationConfigureFormState } from '../types'
import useAppSelector from '../../../store/hooks/useAppSelector'
import Button from '../../app/components/Button'
import checkoutConstraints from '../../checkout/constraints/checkoutConstraints'
import DeliveryLocationField from '../components/deliveryLocationConfigureModal/DeliveryLocationField'
import getCheckoutDeliveryDetails from '../../checkout/utils/getCheckoutDeliveryDetailsFromCustomerDetails'
import FormInput from '../../app/components/FormInput'

import styles from './DeliveryLocationConfigureForm.module.scss'

type CustomerDeliveryAddressWithNote = CustomerDeliveryAddress & {
  note?: string
}

function getInitialDeliveryAddress(
  initialAddress?: CustomerAddress | null,
  outletAddress?: OutletAddress,
) {
  if (initialAddress) {
    return initialAddress as CustomerDeliveryAddressWithNote
  }

  if (outletAddress) {
    return outletAddress as CustomerDeliveryAddressWithNote
  }

  const defaultLocation = {
    latitude: StockholmCoordinates.latitude,
    longitude: StockholmCoordinates.longitude,
  }

  return {
    street: '',
    zipCode: '',
    city: '',
    state: '',
    country: '',
    location: defaultLocation,
  }
}

type DeliveryLocationConfigureFormProps = {
  initialAddress?: CustomerAddress | null
  enableInstructions?: boolean
  onContinue: (orderDelivery?: OrderDelivery) => void
}

export default function DeliveryLocationConfigureForm({
  initialAddress,
  enableInstructions = false,
  onContinue,
}: DeliveryLocationConfigureFormProps) {
  const { t } = useTranslation('common')

  const selectedOutletAddress = useAppSelector(
    outletSelectedOutletAddressSelector,
  )
  const customer = useAppSelector(clientContextCustomerSelector)

  const address = getInitialDeliveryAddress(
    initialAddress,
    selectedOutletAddress,
  )

  const form = useForm<DeliveryLocationConfigureFormState>({
    defaultValues: {
      address: {
        city: address.city || '',
        country: address.country || '',
        location: address.location,
        state: address.state || '',
        street: address.street || '',
        zipCode: address.zipCode || '',
      },
      instructions: address.note || '',
    },
    mode: 'onChange',
  })

  const { formState, handleSubmit } = form
  const { isSubmitting, isValid } = formState

  function handleContinue(values: DeliveryLocationConfigureFormState) {
    const { address: deliveryAddress, instructions } = values

    const checkoutDelivery = getCheckoutDeliveryDetails(
      deliveryAddress,
      instructions,
      customer,
    )

    onContinue(checkoutDelivery)
  }

  return (
    <FormProvider
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...form}
    >
      <div className={styles.formContainer}>
        <div className={styles.formFields}>
          <DeliveryLocationField />
          {enableInstructions && (
            <FormInput
              containerClassName={styles.deliveryInstructions}
              fieldName="instructions"
              autoComplete="off"
              autoCapitalize="none"
              placeholder={t(
                'components.deliveryLocationPickerModal.deliveryInstructionsPlaceholder',
              )}
              label={t(
                'components.deliveryLocationPickerModal.deliveryInstructions',
              )}
              rules={{
                maxLength: {
                  value: checkoutConstraints.max.instructions,
                  message: t('common:validations.maxLength', {
                    limit: checkoutConstraints.max.instructions,
                  }),
                },
              }}
              readOnly={isSubmitting}
              as="textarea"
            />
          )}
        </div>

        <Button
          size="large"
          className={styles.continueButton}
          disabled={!isValid}
          onClick={handleSubmit(handleContinue)}
          data-testid={AppTestIds.DeliveryLocationPickerModal.ContinueButton}
        >
          {t('components.deliveryLocationPickerModal.continue')}
        </Button>
      </div>
    </FormProvider>
  )
}
